import axios from 'axios';
import { useAtom, useSetAtom } from 'jotai';
import { environment } from '../../environment';
import { errorCatcher, useSnackbar } from '../../hooks';
import { useLogin } from '../login';
import { IAuditListItem, IAuditListItemInput, auditListItemsAtom, auditListItemsLoadingAtom } from './types';

type UseAuditList = [IAuditListItem[], {
  list: (query?: Record<string, any>) => Promise<IAuditListItem[] | null>;
  getById: (id: string) => Promise<IAuditListItem | null>;
  getPaginated: (first?: number, offset?: number, query?: Record<string, any>) => Promise<IAuditListItem[] | null>;
  create: (input: IAuditListItemInput) => Promise<IAuditListItem | null>;
  update: (id: string, input: IAuditListItemInput) => Promise<IAuditListItem | null>;
  remove: (id: string) => Promise<IAuditListItem | null>;
}];

const gqlEndpoint = `${environment.baseUrl}/graphql`;
const graphqlRequest = async (authToken: string | null, query: string, variables?: any) => {
  const { data } = await axios.post(gqlEndpoint, { query, variables }, { headers: { 'X-Wallmates-Auth': authToken } });
  if (data.errors) throw new Error(data.errors[0]?.message || 'GraphQL error');
  return data.data;
}

export const useAuditList = (): UseAuditList => {
  const [items, setItems] = useAtom(auditListItemsAtom);
  const setLoading = useSetAtom(auditListItemsLoadingAtom);
  const [, setSnackbar] = useSnackbar();
  const [, authToken, loginFunctions] = useLogin();

  const list = async (query: Record<string, any> = {}) => {
    try { setLoading(true);
      const q = `query($query: JSON){ getTrackableItemAuditLists(query: $query){ id requirement_text position created_at updated_at } }`;
      const data = await graphqlRequest(authToken, q, { query });
      setItems(data.getTrackableItemAuditLists);
      return data.getTrackableItemAuditLists as IAuditListItem[];
    } catch (e:any) { errorCatcher(e, loginFunctions.logout); setSnackbar({show:true,snackbarLevel:'error',text:'Failed to fetch audit list items.'}); return null; }
    finally { setLoading(false);} };

  const getById = async (id: string) => {
    try { const q = `query($id: ID!){ getTrackableItemAuditListById(id:$id){ id requirement_text position created_at updated_at } }`;
      const data = await graphqlRequest(authToken, q, { id });
      return data.getTrackableItemAuditListById as IAuditListItem;
    } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
  };

  const getPaginated = async (first = 20, offset = 0, query: Record<string, any> = {}) => {
    try { const q = `query($first:Int,$offset:Int,$query:JSON){ getPaginatedTrackableItemAuditLists(first:$first,offset:$offset,query:$query){ id requirement_text position created_at updated_at } }`;
      const data = await graphqlRequest(authToken, q, { first, offset, query });
      return data.getPaginatedTrackableItemAuditLists as IAuditListItem[];
    } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
  };

  const create = async (input: IAuditListItemInput) => {
    try { const q = `mutation($input:TrackableItemAuditListInput!){ createTrackableItemAuditList(input:$input){ id } }`;
      const data = await graphqlRequest(authToken, q, { input });
      return data.createTrackableItemAuditList as IAuditListItem;
    } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
  };

  const update = async (id: string, input: IAuditListItemInput) => {
    try { const q = `mutation($id:ID!,$input:TrackableItemAuditListInput!){ updateTrackableItemAuditList(id:$id,input:$input){ id } }`;
      const data = await graphqlRequest(authToken, q, { id, input });
      return data.updateTrackableItemAuditList as IAuditListItem;
    } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
  };

  const remove = async (id: string) => {
    try { const q = `mutation($id:ID!){ deleteTrackableItemAuditList(id:$id){ id } }`;
      const data = await graphqlRequest(authToken, q, { id });
      return data.deleteTrackableItemAuditList as IAuditListItem;
    } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
  };

  return [items, { list, getById, getPaginated, create, update, remove }];
}


